import React, { useState } from 'react';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, Chip, FormControlLabel, Stack, Switch, Typography } from '@mui/material';
import { BankDocumentEntryModel } from '../Model/bankDocument.model';
import {
  buildBankDocumentAllocationSummary,
  formatBankDocumentAllocationSummaryLabel,
} from '../Helpers/allocationSummary';

export const BANK_DOCUMENT_ALLOCATIONS_COLLAPSE_THRESHOLD = 1;

type AllocationModel = BankDocumentEntryModel['allocations'][number];

const formatEntryAmount = (value: number, currency: string): string =>
  `${value.toFixed(2)} ${currency}`;

const resolveTargetLabel = (targetType: AllocationModel['targetType']): string =>
  targetType === 'invoice' ? 'Faktura' : 'Rezerwacja';

export const hasCollapsibleAllocations = (entry: BankDocumentEntryModel): boolean =>
  buildBankDocumentAllocationSummary(entry).allocationCount >
    BANK_DOCUMENT_ALLOCATIONS_COLLAPSE_THRESHOLD && entry.allocations.length > 0;

const pluralize = (count: number, few: string, many: string): string => {
  const lastDigit = count % 10;
  const lastTwoDigits = count % 100;
  const isFew = lastDigit >= 2 && lastDigit <= 4 && !(lastTwoDigits >= 12 && lastTwoDigits <= 14);
  return isFew ? few : many;
};

const resolveAllocationCountLabel = (
  count: number,
  entry: BankDocumentEntryModel,
  typeSummary: AllocationModel['targetType'] | null
): string => {
  if (typeSummary === 'invoice') {
    return `${count} ${pluralize(count, 'faktury', 'faktur')}`;
  }

  if (typeSummary === 'reservation' || entry.allocations.length === 0) {
    return `${count} ${pluralize(count, 'rezerwacje', 'rezerwacji')}`;
  }

  return `${count} ${pluralize(count, 'alokacje', 'alokacji')}`;
};

interface BankDocumentAllocationCellProps {
  entry: BankDocumentEntryModel;
  expanded: boolean;
  onToggle: () => void;
}

/** Collapsed summary of a long allocation list: title, assigned/remaining line and expand chip. */
export const BankDocumentAllocationCell: React.FC<BankDocumentAllocationCellProps> = ({
  entry,
  expanded,
  onToggle,
}) => {
  const summary = buildBankDocumentAllocationSummary(entry);
  const countLabel = resolveAllocationCountLabel(
    summary.allocationCount,
    entry,
    summary.allocationTypeSummary
  );

  return (
    <Stack spacing={0.5} alignItems="flex-start">
      <Typography variant="body2">{formatBankDocumentAllocationSummaryLabel(summary)}</Typography>
      <Typography variant="caption" color="text.secondary">
        Przypisano {summary.allocatedAmount.toFixed(2)} {entry.currency}, pozostało{' '}
        {summary.remainingAmount.toFixed(2)} {entry.currency}
      </Typography>
      <Chip
        size="small"
        variant="outlined"
        clickable
        onClick={onToggle}
        aria-expanded={expanded}
        aria-controls={`bank-document-entry-allocations-${entry.id}`}
        icon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        label={`${countLabel} · ${formatEntryAmount(summary.allocatedAmount, entry.currency)}`}
      />
    </Stack>
  );
};

/** Renders a reservation reference (e.g. "#12 Jan Kowalski") as a link to that reservation. */
export type ReservationLinkRenderer = (reservationId: number, label: string) => React.ReactNode;

interface AllocationDetailItem {
  key: string;
  content: React.ReactNode;
}

// Applicant name is shown for reservation allocations only; blank values are treated as missing.
const resolveApplicantName = (allocation: AllocationModel): string =>
  allocation.targetType === 'reservation'
    ? (allocation.reservationApplicantName?.trim() ?? '')
    : '';

const formatTargetReference = (allocation: AllocationModel): string => {
  const name = resolveApplicantName(allocation);
  return name ? `#${allocation.targetId} ${name}` : `#${allocation.targetId}`;
};

const renderTargetReference = (
  allocation: AllocationModel,
  renderReservationLink?: ReservationLinkRenderer
): React.ReactNode =>
  renderReservationLink && allocation.targetType === 'reservation'
    ? renderReservationLink(allocation.targetId, formatTargetReference(allocation))
    : formatTargetReference(allocation);

const buildPlainItems = (
  entry: BankDocumentEntryModel,
  renderReservationLink?: ReservationLinkRenderer
): AllocationDetailItem[] =>
  entry.allocations.map((allocation) => ({
    key: String(allocation.id),
    content: (
      <>
        {resolveTargetLabel(allocation.targetType)} {renderTargetReference(allocation, renderReservationLink)}
        : {formatEntryAmount(allocation.allocatedAmount, entry.currency)}
      </>
    ),
  }));

// Groups allocations of the same target type and amount; the type prefix is shown only for mixed lists.
const buildGroupedItems = (
  entry: BankDocumentEntryModel,
  renderReservationLink?: ReservationLinkRenderer
): AllocationDetailItem[] => {
  const isMixed = new Set(entry.allocations.map((allocation) => allocation.targetType)).size > 1;
  const groups = new Map<string, AllocationModel[]>();

  entry.allocations.forEach((allocation) => {
    const groupKey = `${allocation.targetType}|${allocation.allocatedAmount.toFixed(2)}`;
    groups.set(groupKey, [...(groups.get(groupKey) ?? []), allocation]);
  });

  return Array.from(groups.entries()).map(([groupKey, members]) => {
    const first = members[0];
    const prefix = isMixed ? `${first.targetType === 'invoice' ? 'Faktury' : 'Rezerwacje'} ` : '';
    return {
      key: groupKey,
      content: (
        <>
          {`${prefix}${formatEntryAmount(first.allocatedAmount, entry.currency)} × ${members.length} (`}
          {members.map((member, index) => (
            <React.Fragment key={member.id}>
              {index > 0 && ', '}
              {renderTargetReference(member, renderReservationLink)}
            </React.Fragment>
          ))}
          )
        </>
      ),
    };
  });
};

interface BankDocumentAllocationDetailsProps {
  entry: BankDocumentEntryModel;
  /** When set, reservation references are rendered through it (e.g. as links). */
  renderReservationLink?: ReservationLinkRenderer;
  /** Keeps the list in one column, for narrow containers such as a table cell. */
  singleColumn?: boolean;
}

/** Full-width allocation grid shown under an entry row, with optional grouping of equal amounts. */
const BankDocumentAllocationDetails: React.FC<BankDocumentAllocationDetailsProps> = ({
  entry,
  renderReservationLink,
  singleColumn = false,
}) => {
  const [grouped, setGrouped] = useState(false);
  const items = grouped
    ? buildGroupedItems(entry, renderReservationLink)
    : buildPlainItems(entry, renderReservationLink);

  return (
    <Stack spacing={1} id={`bank-document-entry-allocations-${entry.id}`} sx={{ py: 1 }}>
      <FormControlLabel
        control={
          <Switch
            size="small"
            checked={grouped}
            onChange={(event) => setGrouped(event.target.checked)}
          />
        }
        label="Grupuj te same kwoty"
      />
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: singleColumn ? '1fr' : { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
          columnGap: 3,
          rowGap: 0.5,
        }}
      >
        {items.map((item) => (
          <Typography key={item.key} variant="caption" color="text.secondary">
            {item.content}
          </Typography>
        ))}
      </Box>
    </Stack>
  );
};

export default BankDocumentAllocationDetails;
